import { useEffect, useState, useRef, useMemo } from "react";
import {
  Container,
  Typography,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Stack,
  Chip,
  Tooltip,
  GlobalStyles,
  Checkbox,
  FormControlLabel,
  RadioGroup,
  Radio,
} from "@mui/material";
import { useLocation, Navigate, useNavigate } from "react-router-dom";
import { getCategory } from "../api/categoryApi";
import html2pdf from "html2pdf.js";
import { fetchReportList } from "../api/answersApi";
import { getQuestionsByDomain } from "../api/questionApi";
import {
  getPatientFromVquraById,
  getPatientById,
  getPatientHistory,
} from "../api/patientApi";
import { getDomainProgress, levelColors } from "../components/CategoryLevels";
import BarChartLevel from "../components/BarChartLevel";
import logo from "../assets/logo.png";

const ProgressReport = () => {
  const location = useLocation();
  const [domains, setDomains] = useState([]);
  const reportRef = useRef();
  const [reportData, setReportData] = useState(null);
  const [therapyDescriptions, setTherapyDescriptions] = useState([]);
  const [concerns, setConcerns] = useState([]);
  const data = location.state;
  const [patientDetails, setPatientDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [checkedDescriptions, setCheckedDescriptions] = useState({});
  const [timeframe, setTimeframe] = useState("1month");

  const navigate = useNavigate();

  useEffect(() => {
    getCategory()
      .then((res) => {
        const data = res.data?.data || [];
        const formatted = data.map((item) => ({
          code: item.code,
          label: item.name,
        }));
        setDomains(formatted);
      })
      .catch((err) => {
        console.error("Failed to fetch categories:", err);
      });
  }, []);

  const { patientId, patientName, staffName, code } = data;

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const res = await getPatientHistory(patientId);
        if (res.data.succeeded) {
          const data = res.data.data;
          setHistory(data);
        }
      } catch (err) {
        console.error("Error fetching history:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [patientId]);

  useEffect(() => {
    const fetchPatient = async () => {
      try {
        let patient = null;

        try {
          const vquraData = await getPatientFromVquraById(patientId);
          if (vquraData) {
            patient = {
              id: vquraData.user_id,
              name: `${vquraData.first_name} ${
                vquraData.last_name || ""
              }`.trim(),
              mobileNo: vquraData.mobile_number,
              dateOfBirth: vquraData.dob,
              gender: vquraData.gender,
              city: vquraData.city,
              state: vquraData.state,
              email: vquraData.email,
              address: vquraData.address,
              source: "vqura",
            };
          }
        } catch (err) {
          console.warn("Vqura lookup failed:", err.message);
        }

        if (!patient) {
          const res = await getPatientById(patientId);
          if (res.data?.succeeded && res.data.data) {
            const p = res.data.data;
            patient = {
              id: p.id,
              name: p.name,
              mobileNo: p.mobileNo,
              dateOfBirth: p.dateOfBirth,
              gender: p.gender,
              city: p.city || "-",
              state: p.state || "-",
              email: p.email,
              address: p.address,
              source: "local",
            };
          }
        }

        setPatientDetails(patient);
      } catch (err) {
        console.error("Error fetching patient:", err);
      }
    };

    fetchPatient();
  }, [patientId]);

  useEffect(() => {
    if (patientId) {
      fetchReportList(patientId)
        .then((res) => {
          const reports = res.data?.data || [];
          if (!reports.length) {
            setReportData(null);
            return;
          }

          const sortedReports = reports.sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );

          const baseReport = {
            ...sortedReports[0],
            domain: [],
          };

          const filledDomains = new Set();

          for (const report of sortedReports) {
            for (const domainItem of report.domain || []) {
              const code = domainItem.domain?.code;
              if (!code) continue;

              if (!filledDomains.has(code) && domainItem.answers?.length) {
                baseReport.domain.push(domainItem);
                filledDomains.add(code);
              }
            }

            if (filledDomains.size === domains.length) break;
          }

          setReportData(baseReport);
        })
        .catch((err) => {
          console.error("Error fetching report list:", err);
        });
    }
  }, [patientId, domains.length]);

  useEffect(() => {
    const fetchTherapyRecommendations = async () => {
      const recommendations = [];
      const concernItems = [];

      for (const domainItem of reportData?.domain || []) {
        const { code } = domainItem.domain;
        const allQuestionsRes = await getQuestionsByDomain(code);
        const allQuestions = allQuestionsRes?.data?.data || [];

        if (code === "SEN" || code === "BEH") {
          const questionsWithAnswers = allQuestions.map((question) => {
            const answer = domainItem.answers.find(
              (a) => a.questionId === question.id
            );
            return {
              ...question,
              staffResponse: answer?.staffResponse ?? null,
              parentResponse: answer?.parentResponse ?? null,
            };
          });

          questionsWithAnswers.forEach((q) => {
            if (q.staffResponse === 2 && q.parentResponse === 2) {
              concernItems.push({
                question: q.question,
                description: q.description,
                domainLabel: domainItem.domain?.name,
              });
            }
          });
        } else {
          const questionsWithAnswers = allQuestions
            .map((question) => {
              const answer = domainItem.answers.find(
                (a) => a.questionId === question.id
              );
              return {
                ...question,
                staffResponse: answer?.staffResponse ?? null,
                parentResponse: answer?.parentResponse ?? null,
              };
            })
            .sort((a, b) => a.position - b.position);

          let lastAttemptedIndex = -1;
          questionsWithAnswers.forEach((q, idx) => {
            if (q.staffResponse !== null || q.parentResponse !== null) {
              lastAttemptedIndex = idx;
            }
          });

          if (lastAttemptedIndex !== -1) {
            for (let i = 0; i <= lastAttemptedIndex; i++) {
              const q = questionsWithAnswers[i];
              const isAchieved =
                q.staffResponse === 2 && q.parentResponse === 2;
              if (!isAchieved) {
                recommendations.push({
                  question: q.question,
                  description: q.description,
                  domainLabel: domainItem.domain?.name,
                });
              }
            }

            const afterLast = questionsWithAnswers.slice(
              lastAttemptedIndex + 1
            );
            const firstUnattempted = afterLast.find(
              (q) => q.staffResponse === null && q.parentResponse === null
            );
            if (firstUnattempted) {
              recommendations.push({
                question: firstUnattempted.question,
                description: firstUnattempted.description,
                domainLabel: domainItem.domain?.name,
              });
            }
          } else {
            const firstQuestion = questionsWithAnswers[0];
            if (firstQuestion) {
              recommendations.push({
                question: firstQuestion.question,
                description: firstQuestion.description,
                domainLabel: domainItem.domain?.name,
              });
            }
          }
        }
      }

      const uniqueRecommendations = recommendations.filter(
        (rec, index, self) =>
          index ===
          self.findIndex(
            (r) =>
              r.question === rec.question && r.domainLabel === rec.domainLabel
          )
      );

      setConcerns(concernItems);
      setTherapyDescriptions(uniqueRecommendations);
    };

    if (reportData?.domain?.length) {
      fetchTherapyRecommendations();
    }
  }, [reportData]);

  const filteredTherapyDescriptions = useMemo(() => {
    if (!therapyDescriptions.length) return [];

    const perDomainLimit = timeframe === "1month" ? 2 : 5;

    const grouped = therapyDescriptions.reduce((acc, item) => {
      const domain = item.domainLabel || "Unknown";
      if (!acc[domain]) acc[domain] = [];
      acc[domain].push(item);
      return acc;
    }, {});

    const limited = Object.values(grouped).flatMap((items) =>
      items.slice(0, perDomainLimit)
    );

    return limited;
  }, [therapyDescriptions, timeframe]);

  useEffect(() => {
    if (filteredTherapyDescriptions.length > 0) {
      const initialState = filteredTherapyDescriptions.reduce((acc, _, idx) => {
        acc[idx] = { question: true, description: false };
        return acc;
      }, {});
      setCheckedDescriptions(initialState);
    } else {
      setCheckedDescriptions({});
    }
  }, [filteredTherapyDescriptions]);

  if (!data) return <Navigate to="/" replace />;

  const handleDownload = () => {
    const element = reportRef.current;
    const excludeSections = element.querySelectorAll(".exclude-pdf");

    // Hide exclude sections
    excludeSections.forEach((sec) => (sec.style.display = "none"));

    // Process therapy goals - handle both questions and descriptions
    const allGoalBlocks = element.querySelectorAll(".therapy-goal-item");

    allGoalBlocks.forEach((block) => {
      const checkbox = block.querySelector('input[type="checkbox"]');
      if (!checkbox) return;

      const label =
        block.querySelector("label") ||
        block.closest(".MuiFormControlLabel-root");

      if (!checkbox.checked) {
        // Hide unchecked items
        block.style.display = "none";
      } else {
        // Keep visible but remove checkbox UI elements
        checkbox.style.display = "none";
        const iconWrapper = label?.querySelector(".MuiCheckbox-root");
        if (iconWrapper) iconWrapper.style.display = "none";

        // Add bullet point for PDF
        if (!label?.querySelector(".pdf-bullet")) {
          const bullet = document.createElement("span");
          bullet.textContent = "• ";
          bullet.style.marginRight = "6px";
          bullet.classList.add("pdf-bullet");
          label.insertBefore(bullet, label.firstChild);
        }
      }
    });

    // Hide chart toolbar
    const charts = document.querySelectorAll(".apexcharts-toolbar");
    charts.forEach((c) => (c.style.display = "none"));

    // Apply page break rules
    const therapyItems = element.querySelectorAll(".therapy-goal-item");
    therapyItems.forEach((item) => {
      item.style.pageBreakInside = "avoid";
      item.style.breakInside = "avoid";
    });

    // Make sure sections stay together
    const concernsSection = element.querySelector(".concerns-section");
    if (concernsSection) {
      concernsSection.style.pageBreakInside = "avoid";
      concernsSection.style.breakInside = "avoid";
    }

    const therapySection = element.querySelector(".therapy-goals-section");
    if (therapySection) {
      therapySection.style.pageBreakInside = "avoid";
      therapySection.style.breakInside = "avoid";
    }

    const doctorRemarks = element.querySelector(".doctor-remarks-section");
    if (doctorRemarks) {
      doctorRemarks.style.pageBreakInside = "avoid";
      doctorRemarks.style.breakInside = "avoid";
    }

    // Chart on new page
    const chartWrapper = element.querySelector(".chart-wrapper");
    if (chartWrapper && !chartWrapper.classList.contains("exclude-pdf")) {
      chartWrapper.style.pageBreakBefore = "always";
      chartWrapper.style.breakBefore = "page";
    }

    const opt = {
      margin: [0.7, 0.5, 0.7, 0.5],
      filename: `${patientName}_Progress_Report.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { 
        scale: 2,
        logging: false,
        useCORS: true
      },
      jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
      pagebreak: { mode: ["avoid-all", "css", "legacy"] },
    };

    html2pdf()
      .set(opt)
      .from(element)
      .toPdf()
      .get("pdf")
      .then((pdf) => {
        const totalPages = pdf.internal.getNumberOfPages();
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        for (let i = 1; i <= totalPages; i++) {
          pdf.setPage(i);
          
          // Header
          pdf.setFillColor(255, 255, 255);
          pdf.rect(0, 0, pageWidth, 0.6, "F");
          pdf.addImage(logo, "PNG", 0.5, 0.15, 1.2, 0.3);
          pdf.setFontSize(10);
          pdf.text("123 Wellness Avenue, Hyderabad", pageWidth - 2.5, 0.25);
          pdf.text("Tel: +91-98765-43210", pageWidth - 2.5, 0.4);
          pdf.text(
            `Generated on ${new Date().toLocaleDateString()}`,
            pageWidth - 2.5,
            0.55
          );

          // Footer
          const footerY = pageHeight - 0.3;
          pdf.setFillColor(255, 255, 255);
          pdf.rect(0, footerY - 0.35, pageWidth, 0.45, "F");
          pdf.setFontSize(9);
          pdf.text(
            "Note: This is an electronically generated summary of attached report.",
            0.5,
            footerY
          );
          pdf.text(`Page ${i} of ${totalPages}`, pageWidth - 1.2, footerY);
        }
      })
      .save()
      .then(() => {
        // Restore all UI elements
        excludeSections.forEach((sec) => (sec.style.display = ""));

        allGoalBlocks.forEach((block) => {
          const checkbox = block.querySelector('input[type="checkbox"]');
          if (!checkbox) return;

          const label =
            block.querySelector("label") ||
            block.closest(".MuiFormControlLabel-root");

          // Restore display
          block.style.display = "";
          checkbox.style.display = "";
          
          const iconWrapper = label?.querySelector(".MuiCheckbox-root");
          if (iconWrapper) iconWrapper.style.display = "";
          
          const bullet = label?.querySelector(".pdf-bullet");
          if (bullet) bullet.remove();
        });

        charts.forEach((c) => (c.style.display = ""));

        therapyItems.forEach((item) => {
          item.style.pageBreakInside = "";
          item.style.breakInside = "";
        });

        if (concernsSection) {
          concernsSection.style.pageBreakInside = "";
          concernsSection.style.breakInside = "";
        }

        if (therapySection) {
          therapySection.style.pageBreakInside = "";
          therapySection.style.breakInside = "";
        }

        if (chartWrapper) {
          chartWrapper.style.pageBreakBefore = "";
          chartWrapper.style.breakBefore = "";
        }

        if (doctorRemarks) {
          doctorRemarks.style.pageBreakInside = "";
          doctorRemarks.style.breakInside = "";
        }
      });
  };

  const calculateAge = (dob) => {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <GlobalStyles
        styles={{
          "@media print": {
            ".exclude-pdf": { display: "none !important" },
            ".apexcharts-menu-icon": { display: "none !important" },
            ".apexcharts-toolbar": { display: "none !important" },
            ".therapy-goal-item": {
              pageBreakInside: "avoid",
              breakInside: "avoid",
            },
            ".concerns-section, .therapy-goals-section, .doctor-remarks-section": {
              pageBreakInside: "avoid !important",
              breakInside: "avoid !important",
            },
          },
        }}
      />

      <Box textAlign="right" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button
            variant="contained"
            onClick={() =>
              navigate(`/domains/${code}`, {
                state: { patientId, patientName },
                replace: true,
              })
            }
          >
            Go Back
          </Button>
          <Button
            variant="contained"
            onClick={() => navigate(`/patient`, { replace: true })}
          >
            Home
          </Button>
          <Button variant="contained" onClick={handleDownload}>
            Download PDF
          </Button>
        </Stack>
      </Box>

      <div ref={reportRef}>
        <Box sx={{ textAlign: "center", mb: 2 }} className="exclude-pdf">
          <img src={logo} alt="Vqure" style={{ height: 40, marginRight: 10 }} />
        </Box>
        <Typography variant="h6" gutterBottom>
          Progress Report
        </Typography>

        <Box sx={{ mb: 2, border: "1px solid #ccc", p: 2 }}>
          <Typography variant="body1">
            <strong>Name:</strong>{" "}
            {reportData?.name ? reportData.name : patientName}
          </Typography>
          <Typography variant="body1">
            <strong>Age:</strong>{" "}
            {patientDetails?.dateOfBirth
              ? calculateAge(patientDetails.dateOfBirth) + " years"
              : ""}
          </Typography>
          <Typography variant="body1">
            <strong>Date of Assessment:</strong>{" "}
            {reportData?.createdAt 
              ? new Date(reportData.createdAt).toLocaleDateString()
              : new Date().toLocaleDateString()}
          </Typography>
          <Typography variant="body1" className="exclude-pdf">
            <strong>Assessor's Name and Designation:</strong>{" "}
            {reportData?.staffName ? reportData.staffName : staffName}
          </Typography>
        </Box>

        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Domain</strong>
              </TableCell>
              <TableCell>Skills Achieved</TableCell>
              <TableCell>Percentage Achieved</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {domains
              .filter(
                (domainItem) =>
                  domainItem.code !== "BEH" && domainItem.code !== "SEN"
              )
              .map((domainItem) => {
                const domainReport = reportData?.domain?.find(
                  (d) => d.domain.code === domainItem.code
                );

                let skillsAchieved = 0;

                if (domainReport && Array.isArray(domainReport.answers)) {
                  domainReport.answers.forEach((answer) => {
                    if (
                      answer.staffResponse === 2 &&
                      answer.parentResponse === 2
                    ) {
                      skillsAchieved++;
                    }
                  });
                }

                const progress = getDomainProgress(
                  domainItem.code,
                  skillsAchieved
                );

                return (
                  <TableRow key={domainItem.code}>
                    <TableCell>{domainItem.label}</TableCell>
                    <TableCell>{skillsAchieved}</TableCell>
                    <TableCell>
                      {progress ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography
                            variant="body2"
                            sx={{ width: 50 }}
                          >{`${progress.percentage}%`}</Typography>
                          <Tooltip title={`Level ${progress.level}`}>
                            <Chip
                              label={`L ${progress.level}`}
                              size="small"
                              sx={{
                                fontWeight: "bold",
                                backgroundColor:
                                  levelColors[progress.level] || "#999",
                                color: "#fff",
                                height: 24,
                              }}
                            />
                          </Tooltip>
                        </Stack>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>

        {/* Concerns Section */}
        <Box 
          className="concerns-section" 
          sx={{ 
            mt: 4, 
            mb: 5,
            pageBreakInside: 'avoid',
            breakInside: 'avoid'
          }}
        >
          <Typography variant="h6">Concerns:</Typography>
          <Box sx={{ border: "1px solid #ccc", minHeight: 80, mt: 1, p: 2 }}>
            {concerns.length > 0 ? (
              <ul style={{ paddingLeft: 20, margin: 0 }}>
                {concerns.map((item, idx) => (
                  <li key={idx} style={{ marginBottom: "12px" }}>
                    <Typography variant="body2">
                      <strong>{item.domainLabel}:</strong> {item.question}
                    </Typography>
                  </li>
                ))}
              </ul>
            ) : (
              <Typography variant="body2" color="text.secondary">
                -
              </Typography>
            )}
          </Box>
        </Box>

        {/* Therapy Goals Section */}
        <Box 
          className="therapy-goals-section" 
          sx={{ 
            mt: 4, 
            mb: 5,
            pageBreakInside: 'avoid',
            breakInside: 'avoid'
          }}
        >
          <Stack direction="row" spacing={3} alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">Therapy Goals:</Typography>
            <RadioGroup
              row
              value={timeframe}
              onChange={(e) => setTimeframe(e.target.value)}
              className="exclude-pdf"
            >
              <FormControlLabel
                value="1month"
                control={<Radio />}
                label="1 Month"
              />
              <FormControlLabel
                value="3month"
                control={<Radio />}
                label="3 Months"
              />
            </RadioGroup>
          </Stack>
          <Box sx={{ border: "1px solid #ccc", minHeight: 80, mt: 1, p: 2 }}>
            {filteredTherapyDescriptions.length > 0 ? (
              <div className="therapy-goals">
                {filteredTherapyDescriptions.map((desc, idx) => (
                  <div key={idx} style={{ marginBottom: "16px" }}>
                    {/* Question checkbox */}
                    <div
                      className="therapy-goal-item"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "8px",
                        pageBreakInside: "avoid",
                        breakInside: "avoid",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={
                              checkedDescriptions[idx]?.question || false
                            }
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              setCheckedDescriptions((prev) => ({
                                ...prev,
                                [idx]: {
                                  question: isChecked,
                                  description: isChecked ? true : false,
                                },
                              }));
                            }}
                            color="primary"
                          />
                        }
                        label={
                          <Typography variant="body2" color="text.secondary">
                            <strong>{desc.domainLabel}:</strong> {desc.question}
                          </Typography>
                        }
                        sx={{ alignItems: "center", margin: 0 }}
                      />
                    </div>

                    {/* Description checkbox */}
                    {desc.description && (
                      <div
                        className="therapy-goal-item"
                        style={{
                          marginBottom: "12px",
                          pageBreakInside: "avoid",
                          breakInside: "avoid",
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={
                                checkedDescriptions[idx]?.description || false
                              }
                              onChange={(e) =>
                                setCheckedDescriptions((prev) => ({
                                  ...prev,
                                  [idx]: {
                                    ...prev[idx],
                                    description: e.target.checked,
                                  },
                                }))
                              }
                              color="primary"
                              disabled={!checkedDescriptions[idx]?.question}
                            />
                          }
                          label={
                            <Typography variant="body2" color="text.secondary">
                              {desc.description}
                            </Typography>
                          }
                          sx={{ alignItems: "center", margin: 0 }}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <Typography variant="body2" color="text.secondary">
                -
              </Typography>
            )}
          </Box>
        </Box>

        {/* Doctor Remarks Section */}
        <Box
          className="doctor-remarks-section"
          sx={{
            mt: 2,
            mb: 5,
            pageBreakInside: "avoid",
            breakInside: "avoid",
          }}
        >
          <Typography variant="h6">Doctor remarks:</Typography>
          <Box
            sx={{
              border: "1px solid #ccc",
              minHeight: 80,
              mt: 1,
              p: 2,
              pageBreakInside: "avoid",
              breakInside: "avoid",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              &nbsp;
            </Typography>
          </Box>
        </Box>

        {/* Chart */}
        <Box
          className="chart-wrapper exclude-pdf"
          sx={{
            mt: 2,
            mb: 5,
            width: "100%",
            maxWidth: "700px",
            mx: "auto",
            pageBreakBefore: "auto",
            breakBefore: "auto",
          }}
        >
          {loading && history.length === 0 ? (
            <Typography>No assessments found.</Typography>
          ) : (
            <BarChartLevel data={history} />
          )}
        </Box>
      </div>
    </Container>
  );
};

export default ProgressReport;